import { useMutation, useQuery } from "@apollo/client";
import { get, isEmpty } from "lodash";
import { GetServerSideProps, InferGetServerSidePropsType } from "next";
import { useRouter } from "next/router";
import { useEffect, useMemo } from "react";
import SEOTags from "~/components/SEOTags";
import { SectionsKey } from "~/constants/enum";
import { SAVE_KEYWORD } from "~/data/home";

import Layout from "~/layouts/ThemeLayout";
import { ProductListProps } from "~/sections/ProductList/ProductList";
import ReMaketing, { REMARKETING_SEARCH } from "~/services/tracking/ReMaketing";
import { initializeApollo } from "~/utils/apollo-client";
import {
  converShortCodeFilterKey,
  convertCleanFilterKey,
} from "~/utils/converters";
import { createSortObject } from "~/utils/filter";
import { transformProductItems } from "~/utils/transform";

import React from "react";
import {
  QUERY_GET_AGGREGATIONS_BY_KEYWORD,
  QUERY_SEARCH_BY_KEYWORD_GQL,
} from "~/data/search/gql";
import { useGetFilterValues } from "~/hooks/useGetFilterValues";
import { trackingSearchPage } from "~/services/tracking";
import { getPageStruct, renderSectionNew } from "~/utils/pageTwo";
import { getTheme } from "~/utils/theme";
import { GET_PRODUCTS_TRACKING } from "~/data/product/gql";
import { Product } from "~/data/gallery-product/interface";

const pageStruct = getPageStruct(getTheme(), "search-result");

const sections: any = pageStruct?.children
  ?.sort((a: any, b: any) => a.order - b.order)
  .map((item: any) => {
    const Section = renderSectionNew(item);

    return {
      id: item.id,
      order: item.order,
      dataKey: item.dataKey,
      component: !!Section ? <Section /> : null,
    };
  });

const SearchResult = ({}: InferGetServerSidePropsType<
  typeof getServerSideProps
>) => {
  const router = useRouter();
  const keyword = router.query.keyword as string;

  //Filter Values
  const {
    filterValues,
    pageSize = 30,
    currentPage = 1,
    sort,
  } = useGetFilterValues();
  const pagination = {
    pageSize: pageSize,
    currentPage,
  };

  const [saveSearchKeyword] = useMutation(SAVE_KEYWORD);

  //Aggregations
  const { data: filters } = useQuery<{ products: FilterType }>(
    QUERY_GET_AGGREGATIONS_BY_KEYWORD,
    {
      variables: {
        search: keyword,
      },
      skip: isEmpty(keyword),
    }
  );

  const { aggregations, sort_fields } = filters?.products || {};
  const filterData = useMemo(() => aggregations, [aggregations]);
  //Products
  const { data: basicProductData, loading } = useQuery(
    QUERY_SEARCH_BY_KEYWORD_GQL,
    {
      variables: {
        ...pagination,
        search: keyword,
        filter: convertCleanFilterKey(filterValues, aggregations),
        sort: sort ?? {},
      },
      onError: ({ message }) =>
        console.error(message, "QUERY_SEARCH_BY_KEYWORD_GQL"),
      skip: isEmpty(keyword),
    }
  );
  const { data: dataCollectionTracking, loading: loadingCollectionTracking } =
    useQuery(GET_PRODUCTS_TRACKING, {
      variables: {
        skus:
          (basicProductData &&
            basicProductData?.products?.items?.map(
              (product: Product) => product.sku + ""
            )) ||
          [],
      },
      context: { fetchOptions: { method: "GET" } },
      skip: !basicProductData || !basicProductData?.products?.items?.length,
    });
  const title = keyword ? `Search results for: '${keyword}'` : "Search results";

  const productItems = useMemo(() => {
    if (
      (loadingCollectionTracking || !dataCollectionTracking) &&
      !!basicProductData
    ) {
      return transformProductItems(get(basicProductData, "products.items", []));
    }

    return transformProductItems(
      get(
        {
          ...basicProductData,
          products: {
            ...basicProductData?.products,
            items: basicProductData?.products?.items?.map((product: any) => {
              const tracking =
                dataCollectionTracking?.productsBySkus?.items?.find(
                  (item: any) => item.sku === product.sku
                );
              return {
                ...product,
                ...tracking,
              };
            }),
          },
        },
        "products.items",
        []
      )
    );
  }, [basicProductData, loadingCollectionTracking, dataCollectionTracking]);

  const sortFields = useMemo(() => sort_fields, [sort_fields]);

  const sectionData = useMemo(() => {
    return {
      [SectionsKey.ProductList]: {
        breadcrumbs: [
          { title: "Home", path: "/" },
          { title: "Search" },
          { title: "Result" },
        ],
        title: title,
        filters: {
          aggregations: filterData,
          sortFields: sortFields,
        },
        data: productItems,
        total: basicProductData?.products?.total_count || 0,
        isLoading: loading,
        identifier: "search",
        listKey: keyword,
        showResult: true,
      } as ProductListProps,
    };
  }, [
    productItems,
    keyword,
    filterData,
    productItems,
    basicProductData?.products?.total_count,
    loading,
    sort,
  ]);

  useEffect(() => {
    if (!!keyword) {
      trackingSearchPage({
        keyword,
        data: productItems,
      });
      saveSearchKeyword({
        variables: {
          keyword,
        },
      });
    }
  }, []);

  return (
    <Layout>
      <SEOTags title={title} />
      <ReMaketing data={{ keyword: keyword }} gtagType={REMARKETING_SEARCH} />
      {sections.map((section: any, index: number) => {
        const Section = section.component;
        return (
          <React.Fragment key={`section-${index}`}>
            {React.isValidElement(Section)
              ? React.cloneElement(Section as React.ReactElement, {
                  ...get(sectionData, section.dataKey, {}),
                })
              : Section}
          </React.Fragment>
        );
      })}
    </Layout>
  );
};

export default SearchResult;

export const getServerSideProps: GetServerSideProps = async ({ query }) => {
  try {
    const apolloClient = await initializeApollo();
    const filter = converShortCodeFilterKey(query as any, "decode");

    const dataSearch = await apolloClient.query({
      query: QUERY_SEARCH_BY_KEYWORD_GQL,
      variables: {
        pageSize: query?.pageSize || 30,
        currentPage: query?.currentPage || 1,
        search: query?.keyword || "",
        filter: convertCleanFilterKey(filter, {}) || {},
        sort: createSortObject(query.sort as string, "-") || {},
      },
    });

    return {
      props: {
        dataSearch,
        initialApolloState: apolloClient.cache.extract(),
      },
    };
  } catch (error) {
    return {
      props: {
        dataSearch: [],
        initialApolloState: null,
      },
    };
  }
};
